import { AdminPreviewMode } from './constants/admin'
import { PermissionType } from './constants/permission'
import type { OrganizationPermission } from './permission'

export type PermissionAbilities = {
  canListReservations: boolean
  canAddOrUpdateReservations: boolean
  canViewReservationsMap: boolean
  canListAdmissions: boolean
  canAddOrUpdateAdmissions: boolean
  canListInternalNotes: boolean
  canAddOrUpdateInternalNotes: boolean
  canDeleteInternalNotes: boolean
  canUpdateOrganization: boolean
  canAddOrUpdateStaff: boolean
  canDeleteStaff: boolean
  canAddOrUpdateApiKeys: boolean
  canAddOrUpdateWebhooks: boolean
  canAddOrUpdateMicrosites: boolean
  canAddOrUpdateDaySettings: boolean
  canUpdateVenueSettings: boolean
  canAddOrUpdateEvents: boolean
  canAddOrUpdateCustomers: boolean
  canAddOrUpdateInventory: boolean
  canListFloorplans: boolean
  canAddOrUpdateFloorplans: boolean
  canUpdateAttributions: boolean
  canSyncInventoryFromLeafLogix: boolean
  canListAdmissionGroupStats: boolean
  canListOrderStats: boolean
  canAddOrUpdatePrinters: boolean
  canAddOrUpdateReviews: boolean
  canListInventory: boolean
  canSuperAdmin: boolean
  isPromoter: boolean
  isAdminPreview: boolean
  adminPreviewMode?: AdminPreviewMode
  adminPreviewLabel?: string
}

export const getDefaultPermissionAbilities = (): PermissionAbilities => ({
  canListReservations: false,
  canAddOrUpdateReservations: false,
  canViewReservationsMap: false,
  canListAdmissions: false,
  canAddOrUpdateAdmissions: false,
  canListInternalNotes: false,
  canAddOrUpdateInternalNotes: false,
  canDeleteInternalNotes: false,
  canUpdateOrganization: false,
  canAddOrUpdateStaff: false,
  canDeleteStaff: false,
  canAddOrUpdateApiKeys: false,
  canAddOrUpdateWebhooks: false,
  canAddOrUpdateMicrosites: false,
  canAddOrUpdateDaySettings: false,
  canUpdateVenueSettings: false,
  canAddOrUpdateEvents: false,
  canAddOrUpdateCustomers: false,
  canAddOrUpdateInventory: false,
  canListFloorplans: false,
  canAddOrUpdateFloorplans: false,
  canUpdateAttributions: false,
  canSyncInventoryFromLeafLogix: false,
  canListAdmissionGroupStats: false,
  canListOrderStats: false,
  canAddOrUpdatePrinters: false,
  canAddOrUpdateReviews: false,
  canListInventory: false,
  canSuperAdmin: false,
  isPromoter: false,
  isAdminPreview: false,
  adminPreviewMode: AdminPreviewMode.NONE,
})

const hasPermissionByVenueId = (
  venueId: string,
  permissions: OrganizationPermission[],
  permissionType: PermissionType
): boolean =>
  !!permissions.find(
    (permission) =>
      permission.venue === venueId && permission.value === permissionType
  )

const hasPermissionByOrganizationId = (
  organizationId: string,
  permissions: OrganizationPermission[],
  permissionType: PermissionType
): boolean =>
  !!permissions.find(
    (permission) =>
      permission.organization === organizationId &&
      permission.value === permissionType
  )

export const getAdminPermissionAbilities = (): PermissionAbilities => ({
  ...getDefaultPermissionAbilities(),
  canUpdateOrganization: true,
  canAddOrUpdateStaff: true,
  canAddOrUpdateCustomers: true,
  canListReservations: true,
  canAddOrUpdateReservations: true,
  canListAdmissions: true,
  canAddOrUpdateAdmissions: true,
  canUpdateAttributions: true,
  canSyncInventoryFromLeafLogix: true,
  canListAdmissionGroupStats: true,
  canListOrderStats: true,
  canAddOrUpdateEvents: true,
  canAddOrUpdateDaySettings: true,
  canUpdateVenueSettings: true,
  canAddOrUpdatePrinters: true,
  canAddOrUpdateReviews: true,
  canAddOrUpdateApiKeys: true,
  canAddOrUpdateWebhooks: true,
  canAddOrUpdateMicrosites: true,
  canListInternalNotes: true,
  canAddOrUpdateInternalNotes: true,
  canDeleteInternalNotes: true,
  canListFloorplans: true,
  canAddOrUpdateFloorplans: true,
  canListInventory: true,
  canAddOrUpdateInventory: true,
  canViewReservationsMap: true,
  canSuperAdmin: true,
})

export const getPermissionAbilitiesByScope = ({
  venueId,
  organizationId,
  permissions,
  isPromoter,
}: {
  venueId: string
  organizationId: string
  permissions: OrganizationPermission[]
  isPromoter: boolean
}): PermissionAbilities => {
  if (!venueId || !organizationId || !permissions.length) {
    return {
      ...getDefaultPermissionAbilities(),
      isPromoter,
    }
  }

  return {
    ...getDefaultPermissionAbilities(),
    canListReservations: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.READ_RESERVATIONS
    ),
    canAddOrUpdateReservations: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.WRITE_RESERVATIONS
    ),
    canListAdmissions: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.READ_GUESTLISTS
    ),
    canAddOrUpdateAdmissions: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.WRITE_GUESTLISTS
    ),
    canListInternalNotes: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.READ_VENUE
    ),
    canListFloorplans: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.READ_VENUE
    ),
    canAddOrUpdateFloorplans: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.WRITE_VENUE
    ),
    canUpdateAttributions: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.UPDATE_ATTRIBUTIONS
    ),
    canAddOrUpdateInventory: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.WRITE_VENUE
    ),
    canSyncInventoryFromLeafLogix: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.WRITE_VENUE
    ),
    canListAdmissionGroupStats: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.READ_PERFORMANCE_REPORTS
    ),
    canListOrderStats: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.READ_PERFORMANCE_REPORTS
    ),
    canAddOrUpdateEvents: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.WRITE_EVENTS
    ),
    canAddOrUpdateDaySettings: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.WRITE_VENUE
    ),
    canUpdateVenueSettings: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.WRITE_VENUE
    ),
    canAddOrUpdatePrinters: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.WRITE_VENUE
    ),
    canAddOrUpdateReviews: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.WRITE_VENUE
    ),
    canAddOrUpdateWebhooks: hasPermissionByVenueId(
      venueId,
      permissions,
      PermissionType.MANAGE_WEBHOOKS
    ),
    canViewReservationsMap: !isPromoter,
    canUpdateOrganization: hasPermissionByOrganizationId(
      organizationId,
      permissions,
      PermissionType.WRITE_ORGANIZATION
    ),
    canAddOrUpdateStaff: hasPermissionByOrganizationId(
      organizationId,
      permissions,
      PermissionType.MANAGE_STAFF
    ),
    canAddOrUpdateCustomers: hasPermissionByOrganizationId(
      organizationId,
      permissions,
      PermissionType.MANAGE_CUSTOMERS
    ),
    canAddOrUpdateApiKeys: hasPermissionByOrganizationId(
      organizationId,
      permissions,
      PermissionType.MANAGE_ORG_API_KEYS
    ),
    canAddOrUpdateMicrosites: true,
    isPromoter,
  }
}
